import type { GridDataSource } from '@mui/x-data-grid-pro';
import { nanoid } from '@reduxjs/toolkit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DataGroup } from '@/features/DataGrid';
import { useDataColumns } from '@/shared/hooks';
import { Analytics } from '@/shared/lib';
import type { TotalData } from '@/shared/ui';
import type { TreeDataResponse } from '../../model/types';

interface UseDataParams {
  dimensions: string[];
  getData: (dataGroups: DataGroup[]) => Promise<TreeDataResponse>;
  getTotal: () => Promise<Record<string, number>>;
}

export const useData = ({
  dimensions,
  getData,
  getTotal,
}: UseDataParams): {
  dataSource: GridDataSource;
  dataColumns: string[];
  total: TotalData | undefined;
} => {
  const dataColumnsComparator = useCallback((a: string, b: string) => {
    return -a.localeCompare(b);
  }, []);
  const { dataColumns, addColumns, reset } = useDataColumns(
    dataColumnsComparator,
  );
  const [totalData, setTotalData] = useState<TotalData | undefined>(undefined);
  const dataSource: GridDataSource = useMemo(() => {
    return {
      getRows: async (params) => {
        Analytics.sendFeatureUsage(
          'tree_data_grid',
          params.groupKeys && params.groupKeys.length > 0
            ? 'fetch_group_rows'
            : 'fetch_rows',
          {
            page: window.location.pathname,
          },
        );
        const dataResopnses = await Promise.all([
          params.groupKeys?.length === 0
            ? getTotal().then((total) => {
                setTotalData(total);
                addColumns(Object.keys(total));

                return {
                  ...total,
                  path: ['total'],
                  isTotal: true,
                };
              })
            : Promise.resolve([]),
          getData(
            params.groupKeys?.map((groupKey, index) => {
              return {
                dimension: dimensions[index],
                value: groupKey,
              };
            }) || [],
          ).then(({ rows, columns }) => {
            addColumns(columns);

            return rows.map((row, ind) => {
              return {
                ...row,
                firstInGroup: ind === 0,
                lastInGroup: ind === rows.length - 1,
              };
            });
          }),
        ]);

        return {
          rows: dataResopnses.flat().map((row) => {
            return {
              id: nanoid(),
              ...row,
            };
          }),
        };
      },
      getGroupKey: (row) => {
        if (row.isTotal) {
          return row.path.at(-1);
        }

        const dimension = dimensions[row.path.length - 1];

        return row[dimension];
      },
      getChildrenCount: (row) => {
        if (row.isTotal || row.path.length >= dimensions.length) {
          return 0;
        }

        return 1;
      },
    };
  }, [getData, getTotal, addColumns, dimensions]);

  useEffect(() => {
    reset();
  }, [getData, getTotal, reset]);

  return {
    total: totalData,
    dataSource,
    dataColumns,
  };
};
